import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { StatCard } from './components/StatCard';
import { KpiTimeChart } from './components/KpiTimeChart';
import { WIDGETS, WIDGETS_BY_ID, SECCIONES_LABEL, computeWidget, widgetPeriodos } from './dashboard/widgets';
import { exportDashboardPDF } from './dashboard/exportPdf';
import { fmtPeriodo } from './components/kpiUtils';
import {
  LayoutGrid, Plus, Pencil, Save, X, ArrowUp, ArrowDown, Trash2, FileDown, Lock, Rows3, SquareStack,
  DollarSign, Download as DownloadIcon, Gift, UserCheck, ShieldBan, UserPlus, Banknote, PiggyBank, LifeBuoy,
} from 'lucide-react';

const SECCION_ICON = {
  'kpis-ingresos': DollarSign, 'kpis-adquisicion': DownloadIcon, 'kpis-adopcion': Gift,
  'kpis-clientes-activos': UserCheck, 'kpis-bloqueos': ShieldBan, 'kpis-afiliacion': UserPlus,
  'kpis-prestamos': Banknote, 'kpis-cuenta-remunerada': PiggyBank, 'kpis-atencion': LifeBuoy,
};

const KpisMiTablero = () => {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scope = 'personal';
  const [edit, setEdit] = useState(false);
  const [working, setWorking] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branding, setBranding] = useState({ nombre_empresa: 'AlertGuard', logo: null });
  const gridRef = useRef(null);

  const load = async () => {
    try {
      const [cfg, dat] = await Promise.all([
        api.get('/kpis/dashboard/config'),
        api.get('/kpis/dashboard/data'),
      ]);
      setConfig(cfg.data);
      setData(dat.data);
    } catch (e) { toast.error('Error al cargar el tablero'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get('/branding/public')
      .then((r) => setBranding({ nombre_empresa: r.data?.nombre_empresa || 'AlertGuard', logo: r.data?.logo_header || r.data?.logo_login || null }))
      .catch(() => {});
  }, []);

  const es_admin = config?.es_admin;
  const permitidas = config?.secciones_permitidas || [];
  const canView = (seccion) => !!es_admin || permitidas.includes(seccion);
  const canEdit = true;

  const scopeWidgets = useMemo(() => {
    if (!config) return [];
    return config.personal?.widgets || [];
  }, [config]);

  const personalExiste = !!config?.personal;
  const displayWidgets = edit ? working : scopeWidgets;

  const startEdit = (base) => { setWorking((base || scopeWidgets).map((w) => ({ ...w }))); setEdit(true); };
  const cancelEdit = () => { setEdit(false); setWorking([]); };
  const addWidget = (wid) => { setWorking((prev) => [...prev, { wid, periodo: null }]); setAddOpen(false); };
  const addLayout = (tipo) => {
    setWorking((prev) => [...prev, { tipo, titulo: tipo === 'separador' ? 'Nueva sección' : 'Grupo de widgets' }]);
    setAddOpen(false);
  };
  const removeAt = (i) => setWorking((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setWorking((prev) => {
    const a = [...prev]; const j = i + dir; if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const setPeriodo = (i, periodo) => setWorking((prev) => prev.map((w, idx) => (idx === i ? { ...w, periodo: periodo || null } : w)));
  const setTitulo = (i, titulo) => setWorking((prev) => prev.map((w, idx) => (idx === i ? { ...w, titulo } : w)));

  const save = async () => {
    setSaving(true);
    try {
      const clean = working
        .filter((w) => w.tipo === 'separador' || w.tipo === 'contenedor' || WIDGETS_BY_ID[w.wid])
        .map((w) => (w.tipo ? { tipo: w.tipo, titulo: w.titulo || '' } : { wid: w.wid, periodo: w.periodo || null }));
      await api.put('/kpis/dashboard/config', { scope, widgets: clean });
      setConfig((prev) => ({ ...prev, [scope]: { widgets: clean } }));
      setEdit(false);
      toast.success(scope === 'sistema' ? 'Tablero del sistema guardado' : 'Tu tablero fue guardado');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const list = scopeWidgets || [];
      const items = [];
      for (let i = 0; i < list.length; i += 1) {
        const it = list[i];
        if (it?.tipo === 'separador') { items.push({ type: 'separador', titulo: it.titulo || '' }); continue; }
        if (it?.tipo === 'contenedor') { items.push({ type: 'contenedor', titulo: it.titulo || '' }); continue; }
        const meta = WIDGETS_BY_ID[it.wid];
        if (!meta || !canView(meta.seccion)) continue;
        const r = computeWidget(it.wid, data, it);
        if (!r) continue;
        if (r.kind === 'kpi') {
          items.push({ type: 'kpi', label: r.label, value: r.value, deltaLabel: r.deltaLabel, accent: r.accent });
        } else {
          const node = gridRef.current?.querySelector(`[data-pdf-chartidx="${i}"]`);
          items.push({ type: 'chart', title: r.title, accent: r.accent, node });
        }
      }
      if (!items.length) { toast.error('No hay nada para exportar'); return; }
      await exportDashboardPDF(items, {
        titulo: scope === 'sistema' ? 'Tablero del sistema' : 'Mi tablero',
        empresa: branding.nombre_empresa, logo: branding.logo,
      });
      toast.success('Informe PDF generado');
    } catch (e) { toast.error('No se pudo exportar el PDF'); }
    finally { setExporting(false); }
  };

  const catalogoDisponible = useMemo(() => {
    const grupos = {};
    WIDGETS.filter((w) => canView(w.seccion)).forEach((w) => {
      (grupos[w.seccion] = grupos[w.seccion] || []).push(w);
    });
    return grupos;
  }, [config]);

  // Agrupa los items en bloques: separadores, contenedores (agrupan los widgets
  // que les siguen hasta el próximo separador/contenedor) y grillas "sueltas".
  const blocks = useMemo(() => {
    const list = displayWidgets || [];
    const out = [];
    let cont = null;
    list.forEach((item, idx) => {
      if (item?.tipo === 'separador') {
        cont = null;
        out.push({ kind: 'separador', item, idx });
      } else if (item?.tipo === 'contenedor') {
        cont = { kind: 'contenedor', item, idx, children: [] };
        out.push(cont);
      } else {
        const entry = { item, idx };
        if (cont) cont.children.push(entry);
        else {
          const lastb = out[out.length - 1];
          if (lastb && lastb.kind === 'loose') lastb.children.push(entry);
          else out.push({ kind: 'loose', children: [entry] });
        }
      }
    });
    return out;
  }, [displayWidgets]);

  const layoutControls = (idx, prefix) => (
    <div className="flex gap-1 bg-white/95 dark:bg-slate-900/95 rounded-lg border border-slate-200 dark:border-slate-700 p-1 shadow-sm shrink-0">
      <button onClick={() => move(idx, -1)} className="p-1 text-slate-500 hover:text-indigo-600" title="Subir" data-testid={`mitablero-${prefix}-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
      <button onClick={() => move(idx, 1)} className="p-1 text-slate-500 hover:text-indigo-600" title="Bajar" data-testid={`mitablero-${prefix}-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      <button onClick={() => removeAt(idx)} className="p-1 text-slate-500 hover:text-rose-600" title="Quitar" data-testid={`mitablero-${prefix}-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );

  const renderWidget = (cfg, idx) => {
    const meta = WIDGETS_BY_ID[cfg.wid];
    if (!meta || !canView(meta.seccion)) return null;
    const r = computeWidget(cfg.wid, data, cfg);
    if (!r) return null;
    const isChart = meta.tipo === 'chart';
    const Icon = SECCION_ICON[meta.seccion];
    return (
      <div key={`${cfg.wid}-${idx}`} className={`relative ${isChart ? 'md:col-span-2' : ''}`}
        data-pdf-item data-pdf-span={isChart ? '2' : '1'} data-testid={`mitablero-widget-${cfg.wid}`}>
        {edit && (
          <div className="absolute z-20 top-2 right-2 flex gap-1 bg-white/95 dark:bg-slate-900/95 rounded-lg border border-slate-200 dark:border-slate-700 p-1 shadow-sm">
            <button onClick={() => move(idx, -1)} className="p-1 text-slate-500 hover:text-indigo-600" title="Subir" data-testid={`mitablero-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => move(idx, 1)} className="p-1 text-slate-500 hover:text-indigo-600" title="Bajar" data-testid={`mitablero-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button onClick={() => removeAt(idx)} className="p-1 text-slate-500 hover:text-rose-600" title="Quitar" data-testid={`mitablero-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {edit && meta.periodica && (
          <div className="absolute z-20 top-2 left-2">
            <select value={cfg.periodo || ''} onChange={(e) => setPeriodo(idx, e.target.value)}
              className="text-xs border border-slate-300 dark:border-slate-600 rounded-md px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
              data-testid={`mitablero-periodo-${idx}`}>
              <option value="">Último mes</option>
              {widgetPeriodos(cfg.wid, data).map((p) => <option key={p} value={p}>{fmtPeriodo(p)}</option>)}
            </select>
          </div>
        )}
        {r.kind === 'kpi' ? (
          <StatCard label={r.label} value={r.value} icon={Icon} accent={r.accent} deltaLabel={r.deltaLabel} testId={`mitablero-kpi-${cfg.wid}`} />
        ) : (
          <Card className="p-5 lg:p-6" data-pdf-chartidx={idx} data-testid={`mitablero-chart-card-${cfg.wid}`}>
            <div className="flex items-center gap-2 mb-3">
              {Icon && <Icon className="h-4 w-4" style={{ color: r.accent }} />}
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{r.title}</h3>
            </div>
            <KpiTimeChart {...r.chartProps} testId={`mitablero-chart-${cfg.wid}`} />
          </Card>
        )}
      </div>
    );
  };

  const renderSeparador = (cfg, idx, key) => (
    <div key={`s-${key}`} className="relative pt-2" data-pdf-item data-pdf-span="2" data-testid={`mitablero-sep-${idx}`}>
      <div className="flex items-center gap-3">
        {edit ? (
          <input value={cfg.titulo || ''} onChange={(e) => setTitulo(idx, e.target.value)} placeholder="Título de la sección"
            className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 bg-transparent border-b border-dashed border-slate-300 dark:border-slate-600 focus:outline-none focus:border-indigo-500 px-1 py-0.5 min-w-[160px]"
            data-testid={`mitablero-sep-title-${idx}`} />
        ) : (
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 whitespace-nowrap">{cfg.titulo || 'Sección'}</h2>
        )}
        <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
        {edit && layoutControls(idx, 'sep')}
      </div>
    </div>
  );

  const renderContHeader = (cfg, idx) => (
    <div className="relative flex items-center gap-3" data-pdf-item data-pdf-span="2">
      <div className="h-7 w-1.5 rounded-full bg-indigo-500 shrink-0" />
      {edit ? (
        <input value={cfg.titulo || ''} onChange={(e) => setTitulo(idx, e.target.value)} placeholder="Título del grupo"
          className="flex-1 text-lg font-semibold text-slate-800 dark:text-slate-100 bg-transparent border-b border-dashed border-slate-300 dark:border-slate-600 focus:outline-none focus:border-indigo-500 px-1 py-0.5"
          data-testid={`mitablero-cont-title-${idx}`} />
      ) : (
        <h2 className="flex-1 text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-100">{cfg.titulo || 'Grupo'}</h2>
      )}
      {edit && layoutControls(idx, 'cont')}
    </div>
  );

  if (loading) return <div className="p-8 text-slate-500" data-testid="mitablero-loading">Cargando tablero…</div>;

  const empty = displayWidgets.length === 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="kpis-mi-tablero">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><LayoutGrid className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Mi Tablero</h1>
            <p className="text-sm text-slate-500">Armá tu panel con las métricas que te interesan y exportá el informe</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={doExport} disabled={exporting || empty || edit} data-testid="mitablero-export-btn">
            <FileDown className="h-4 w-4 mr-1" /> {exporting ? 'Generando…' : 'Exportar PDF'}
          </Button>

          {!edit && canEdit && (
            <Button onClick={() => startEdit()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mitablero-edit-btn">
              <Pencil className="h-4 w-4 mr-1" /> Editar
            </Button>
          )}
          {!edit && !canEdit && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid="mitablero-solo-lectura"><Lock className="h-3.5 w-3.5" /> Solo lectura (lo edita un administrador)</span>
          )}
          {edit && (
            <>
              <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="mitablero-add-open-btn"><Plus className="h-4 w-4 mr-1" /> Agregar widget</Button>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mitablero-save-btn"><Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar'}</Button>
              <Button variant="ghost" onClick={cancelEdit} data-testid="mitablero-cancel-btn"><X className="h-4 w-4 mr-1" /> Cancelar</Button>
            </>
          )}
        </div>
      </div>

      {/* Estado vacío del tablero personal */}
      {scope === 'personal' && !personalExiste && !edit ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="mitablero-personal-empty">
          <LayoutGrid className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Todavía no creaste tu tablero personal.</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Empezá a partir del tablero del sistema y ajustalo a tu gusto.</p>
          <Button onClick={() => startEdit(config?.sistema?.widgets || [])} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mitablero-crear-personal-btn">
            <Plus className="h-4 w-4 mr-1" /> Crear mi tablero
          </Button>
        </Card>
      ) : empty ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="mitablero-empty">
          <LayoutGrid className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Este tablero no tiene widgets.</p>
          {edit && <Button onClick={() => setAddOpen(true)} className="mt-4 bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> Agregar widget</Button>}
        </Card>
      ) : (
        <div ref={gridRef} className="space-y-4 bg-transparent" data-testid="mitablero-grid">
          {blocks.map((b, bi) => {
            if (b.kind === 'separador') return renderSeparador(b.item, b.idx, bi);
            if (b.kind === 'contenedor') {
              return (
                <div key={`c-${bi}`} data-testid={`mitablero-contenedor-${b.idx}`}
                  className="rounded-2xl border-2 border-indigo-100 dark:border-slate-700 bg-indigo-50/30 dark:bg-slate-800/30 p-4 lg:p-5">
                  {renderContHeader(b.item, b.idx)}
                  {b.children.length ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                      {b.children.map((ch) => renderWidget(ch.item, ch.idx))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-3">Contenedor vacío — los widgets que agregues (o muevas) debajo se agruparán en esta sección hasta el próximo separador o contenedor.</p>
                  )}
                </div>
              );
            }
            return (
              <div key={`l-${bi}`} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {b.children.map((ch) => renderWidget(ch.item, ch.idx))}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: agregar widget */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="mitablero-add-dialog">
          <DialogHeader>
            <DialogTitle>Agregar al tablero</DialogTitle>
            <DialogDescription className="sr-only">Elegí widgets o elementos de diseño para agregar a tu tablero</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Diseño: separadores y contenedores */}
            <div>
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Rows3 className="h-4 w-4 text-slate-400" /> Diseño
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button onClick={() => addLayout('separador')}
                  className="flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors"
                  data-testid="mitablero-add-separador">
                  <span className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"><Rows3 className="h-4 w-4 text-slate-400" /> Separador con título</span>
                  <Plus className="h-4 w-4 text-indigo-500" />
                </button>
                <button onClick={() => addLayout('contenedor')}
                  className="flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors"
                  data-testid="mitablero-add-contenedor">
                  <span className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"><SquareStack className="h-4 w-4 text-slate-400" /> Contenedor de sección</span>
                  <Plus className="h-4 w-4 text-indigo-500" />
                </button>
              </div>
            </div>

            {Object.keys(catalogoDisponible).length === 0 && (
              <p className="text-sm text-slate-400">No tenés secciones habilitadas para agregar widgets.</p>
            )}
            {Object.entries(catalogoDisponible).map(([seccion, ws]) => {
              const Icon = SECCION_ICON[seccion];
              return (
                <div key={seccion}>
                  <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {Icon && <Icon className="h-4 w-4 text-slate-400" />} {SECCIONES_LABEL[seccion]}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ws.map((w) => (
                      <button key={w.wid} onClick={() => addWidget(w.wid)}
                        className="flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors"
                        data-testid={`mitablero-add-${w.wid}`}>
                        <span className="text-sm text-slate-700 dark:text-slate-200">{w.titulo}</span>
                        <span className="flex items-center gap-1 shrink-0">
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${w.tipo === 'chart' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>{w.tipo === 'chart' ? 'gráfico' : 'métrica'}</span>
                          <Plus className="h-4 w-4 text-indigo-500" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KpisMiTablero;
